import type { PatternRepository, StorageFailure } from '@miyuki/domain';
import type { BraceletPatternDto, EntityId, PatternSummaryDto } from '@miyuki/types';
import * as React from 'react';

export interface SavedDesigns {
  readonly designs: readonly PatternSummaryDto[];
  readonly lastFailure: StorageFailure | null;
  readonly refresh: () => Promise<void>;
  /** Resumen del diseño que ya usa ese nombre, excluyendo `exceptId`. */
  readonly findConflict: (name: string, exceptId?: EntityId) => Promise<PatternSummaryDto | null>;
  readonly save: (pattern: BraceletPatternDto) => Promise<BraceletPatternDto | null>;
  readonly open: (id: EntityId) => Promise<BraceletPatternDto | null>;
  readonly rename: (id: EntityId, name: string) => Promise<boolean>;
  readonly duplicate: (id: EntityId, name: string) => Promise<boolean>;
  readonly remove: (id: EntityId) => Promise<void>;
}

/**
 * Lista y gestión de los diseños guardados.
 *
 * Los fallos del almacenamiento no se lanzan: se exponen en `lastFailure` para
 * que la interfaz los cuente. Un guardado que falla nunca se da por hecho
 * (RF-44).
 *
 * Detectar la colisión de nombre es cosa de quien llama: `findConflict`
 * responde, la interfaz pide confirmación y recién entonces se guarda o se
 * renombra. Así guardar y renombrar se comportan igual (RF-34, RF-37).
 */
export function useSavedDesigns(repository: PatternRepository): SavedDesigns {
  const [designs, setDesigns] = React.useState<readonly PatternSummaryDto[]>([]);
  const [lastFailure, setLastFailure] = React.useState<StorageFailure | null>(null);

  const refresh = React.useCallback(async () => {
    setDesigns(await repository.list());
  }, [repository]);

  // La carga inicial es una suscripción a un sistema externo: el estado se
  // actualiza en el callback de la promesa, ya fuera del cuerpo del efecto.
  React.useEffect(() => {
    let cancelled = false;

    repository.list().then(
      (list) => {
        if (!cancelled) setDesigns(list);
      },
      () => {
        // `list` nunca falla: devuelve lo que pudo leer.
      },
    );

    return () => {
      cancelled = true;
    };
  }, [repository]);

  const findConflict = React.useCallback(
    async (name: string, exceptId?: EntityId) => {
      const match = await repository.findByName(name);
      return match && match.id !== exceptId ? match : null;
    },
    [repository],
  );

  const save = React.useCallback(
    async (pattern: BraceletPatternDto) => {
      const result = await repository.save(pattern);
      if (!result.ok) {
        setLastFailure(result.error);
        return null;
      }

      setLastFailure(null);
      await refresh();
      return result.value;
    },
    [refresh, repository],
  );

  const open = React.useCallback(
    async (id: EntityId) => {
      const result = await repository.load(id);
      if (!result.ok) {
        setLastFailure(result.error);
        return null;
      }

      setLastFailure(null);
      return result.value;
    },
    [repository],
  );

  const rename = React.useCallback(
    async (id: EntityId, name: string) => {
      const result = await repository.rename(id, name);
      if (!result.ok) {
        setLastFailure(result.error);
        return false;
      }

      setLastFailure(null);
      await refresh();
      return true;
    },
    [refresh, repository],
  );

  const duplicate = React.useCallback(
    async (id: EntityId, name: string) => {
      const result = await repository.duplicate(id, name);
      if (!result.ok) {
        setLastFailure(result.error);
        return false;
      }

      setLastFailure(null);
      await refresh();
      return true;
    },
    [refresh, repository],
  );

  const remove = React.useCallback(
    async (id: EntityId) => {
      await repository.remove(id);
      await refresh();
    },
    [refresh, repository],
  );

  return { designs, lastFailure, refresh, findConflict, save, open, rename, duplicate, remove };
}
